import * as XLSX from "xlsx";
import { BOAT, SHORE } from "./cfsa-data";
import juniorCatchReleaseRecordsData from "./data/juniorCatchReleaseRecords.json";
import juniorRecordsData from "./data/juniorRecords.json";
import { buildRecordsIndex, kgToLbsOzDrmsStr, getDisplaySpecies, isNotHeavier } from "./lib/recordMatching";

const BOAT_SPECIES_NAMES = BOAT.map((r) => r.species);
const SHORE_SPECIES_NAMES = SHORE.map((r) => r.species);

function juniorCatchAndReleaseEntries() {
  return juniorCatchReleaseRecordsData.map((r, i) => ({ speciesLabel: r.species, ...r, ref: i }));
}

function juniorOfficialEntries() {
  return juniorRecordsData.map((r, i) => ({ speciesLabel: r.species, ...r, ref: i }));
}

async function seniorEntries() {
  const res = await fetch("/rsaa_catch_rlease_record.xlsx");
  const buffer = await res.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const entries = [];
  rows.forEach((row, i) => {
    const speciesLabel = String(row[4] || "").trim();
    if (!speciesLabel || /^SPECIES?$/i.test(speciesLabel)) return;
    entries.push({
      speciesLabel,
      boatYear: row[0],
      boatCaptor: row[1],
      boatWeight: row[2],
      shoreYear: row[6],
      shoreCaptor: row[7],
      shoreWeight: row[8],
      ref: i,
    });
  });
  return entries;
}

async function officialEntries() {
  const res = await fetch("/rsaa_records_2025.xls");
  const buffer = await res.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const entries = [];
  wb.SheetNames.forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    rows.forEach((row, i) => {
      const speciesLabel = String(row[4] || "").trim();
      if (!speciesLabel || /^SPECIE$/i.test(speciesLabel) || /^SPECIES?$/i.test(speciesLabel)) return;
      entries.push({
        speciesLabel,
        boatYear: row[0],
        boatCaptor: row[1],
        boatWeight: row[2],
        shoreYear: row[6],
        shoreCaptor: row[7],
        shoreWeight: row[8],
        ref: i,
      });
    });
  });
  return entries;
}

let cachedIndexes = null;

function toYear(value) {
  return String(value || "").slice(0, 4);
}

// A junior's catch (C&R or official) is eligible to hold both the junior
// record and the senior record for that category; a senior's catch only
// ever contends for the senior record.
function recordScopesForCatch(catchRow) {
  return catchRow.is_junior ? ["junior", "senior"] : ["senior"];
}

function matchRecordForIndex(index, catchRow, recordType) {
  const existing = index.get(`${catchRow.species}::${catchRow.location}`);
  if (!existing) {
    const validNames = catchRow.location === "boat" ? BOAT_SPECIES_NAMES : SHORE_SPECIES_NAMES;
    if (!validNames.includes(catchRow.species)) return null;
    return {
      previous: { species: catchRow.species, location: catchRow.location, kg: null, year: "", captor: "" },
      recordType,
    };
  }
  if (existing.kg != null && isNotHeavier(catchRow.weight_kg, existing.kg)) return null;
  return { previous: existing, recordType };
}

export function updateRecordEntry(entry, catchRow, location, recordType) {
  if (!entry || !catchRow || !catchRow.species || !catchRow.location || catchRow.weight_kg == null) return null;
  const isJunior = catchRow.is_junior;
  if (recordType === "catchAndRelease" && !isJunior) {
    entry.boatYear = toYear(catchRow.weigh_date);
    entry.boatCaptor = catchRow.angler_name;
    entry.boatWeight = kgToLbsOzDrmsStr(catchRow.weight_kg);
    entry.shoreYear = toYear(catchRow.weigh_date);
    entry.shoreCaptor = catchRow.angler_name;
    entry.shoreWeight = kgToLbsOzDrmsStr(catchRow.weight_kg);
    return entry;
  }
  if (location === "boat") {
    entry.boatYear = toYear(catchRow.weigh_date);
    entry.boatCaptor = catchRow.angler_name;
    entry.boatWeight = kgToLbsOzDrmsStr(catchRow.weight_kg);
  } else {
    entry.shoreYear = toYear(catchRow.weigh_date);
    entry.shoreCaptor = catchRow.angler_name;
    entry.shoreWeight = kgToLbsOzDrmsStr(catchRow.weight_kg);
  }
  return entry;
}

export function resolveRecordEntries(catches, indexes) {
  if (!catches || !indexes) return { senior: [], junior: [] };
  const senior = []; const junior = [];
  catches.forEach((catchRow) => {
    if (!catchRow.species || !catchRow.location || catchRow.weight_kg == null) return;
    const recordSet = catchRow.is_catch_and_release ? indexes.catchAndRelease : indexes.official;
    if (!recordSet) return;
    const recordType = catchRow.is_catch_and_release ? "catchAndRelease" : "official";
    recordScopesForCatch(catchRow).forEach((scope) => {
      const index = recordSet[scope];
      const existing = index.get(`${catchRow.species}::${catchRow.location}`);
      if (!existing) return;
      if (existing.kg != null && isNotHeavier(catchRow.weight_kg, existing.kg)) return;
      const entry = { catch: catchRow, previous: existing, recordType };
      (scope === "junior" ? junior : senior).push(entry);
    });
  });
  return { senior, junior };
}

export function applyRecordUpdatesToIndexes(indexes, catches) {
  if (!indexes || !Array.isArray(catches)) return indexes;
  const nextIndexes = {
    catchAndRelease: {
      senior: new Map(indexes.catchAndRelease?.senior || []),
      junior: new Map(indexes.catchAndRelease?.junior || []),
    },
    official: {
      senior: new Map(indexes.official?.senior || []),
      junior: new Map(indexes.official?.junior || []),
    },
  };

  const updateIndex = (index, catchRow) => {
    const key = `${catchRow.species}::${catchRow.location}`;
    const existing = index.get(key);
    if (!existing) {
      index.set(key, {
        species: catchRow.species,
        location: catchRow.location,
        kg: catchRow.weight_kg,
        year: toYear(catchRow.weigh_date),
        captor: catchRow.angler_name,
      });
      return;
    }
    if (existing.kg != null && isNotHeavier(catchRow.weight_kg, existing.kg)) return;
    index.set(key, {
      ...existing,
      species: catchRow.species,
      location: catchRow.location,
      kg: catchRow.weight_kg,
      year: toYear(catchRow.weigh_date),
      captor: catchRow.angler_name,
      weightRaw: null,
    });
  };

  catches.forEach((catchRow) => {
    if (!catchRow.species || !catchRow.location || catchRow.weight_kg == null) return;
    const recordSet = catchRow.is_catch_and_release ? nextIndexes.catchAndRelease : nextIndexes.official;
    recordScopesForCatch(catchRow).forEach((scope) => updateIndex(recordSet[scope], catchRow));
  });

  return nextIndexes;
}

export function buildDisplayRecords(indexes, recordType = "catchAndRelease", scope = "senior") {
  if (!indexes?.[recordType]?.[scope]) return [];
  const rowsBySpecies = new Map();

  indexes[recordType][scope].forEach((entry) => {
    if (!entry?.species) return;
    const displaySpecies = getDisplaySpecies(entry.species);
    const existing = rowsBySpecies.get(displaySpecies) || {
      species: displaySpecies,
      boatYear: "",
      boatCaptor: "",
      boatWeight: "",
      shoreYear: "",
      shoreCaptor: "",
      shoreWeight: "",
    };

    const weight = entry.weightRaw ?? (entry.kg != null ? kgToLbsOzDrmsStr(entry.kg) : "");
    const year = entry.year != null ? String(entry.year) : "";
    const captor = entry.captor != null ? String(entry.captor) : "";

    if (entry.location === "boat") {
      existing.boatYear = year;
      existing.boatCaptor = captor;
      existing.boatWeight = weight;
    } else {
      existing.shoreYear = year;
      existing.shoreCaptor = captor;
      existing.shoreWeight = weight;
    }

    rowsBySpecies.set(displaySpecies, existing);
  });

  return Array.from(rowsBySpecies.values()).sort((a, b) => a.species.localeCompare(b.species));
}

export function evaluateRecordChecks(catches, indexes) {
  if (!indexes || !Array.isArray(catches)) return {};
  const checks = {};
  const orderedCatches = [...catches].sort((a, b) => {
    const aTime = new Date(a.weigh_date || a.created_at || 0).getTime();
    const bTime = new Date(b.weigh_date || b.created_at || 0).getTime();
    return aTime - bTime;
  });
  const workingIndexes = {
    catchAndRelease: {
      senior: new Map(indexes.catchAndRelease?.senior || []),
      junior: new Map(indexes.catchAndRelease?.junior || []),
    },
    official: {
      senior: new Map(indexes.official?.senior || []),
      junior: new Map(indexes.official?.junior || []),
    },
  };

  orderedCatches.forEach((catchRow) => {
    const check = checkAgainstRecord(catchRow, workingIndexes);
    checks[catchRow.id] = check;
    if (check) {
      const recordSet = catchRow.is_catch_and_release ? workingIndexes.catchAndRelease : workingIndexes.official;
      check.forEach(({ scope }) => {
        recordSet[scope].set(`${catchRow.species}::${catchRow.location}`, {
          species: catchRow.species,
          location: catchRow.location,
          kg: catchRow.weight_kg,
          year: toYear(catchRow.weigh_date),
          captor: catchRow.angler_name,
        });
      });
    }
  });

  return checks;
}

// Loads and caches the senior + junior record indexes for the lifetime of the page.
export async function loadRecordIndexes() {
  if (cachedIndexes) return cachedIndexes;
  const [catchAndReleaseSenior, catchAndReleaseJunior, officialSenior] = await Promise.all([
    seniorEntries(),
    Promise.resolve(juniorCatchAndReleaseEntries()),
    officialEntries(),
  ]);
  cachedIndexes = {
    catchAndRelease: {
      senior: buildRecordsIndex(catchAndReleaseSenior, BOAT_SPECIES_NAMES, SHORE_SPECIES_NAMES),
      junior: buildRecordsIndex(catchAndReleaseJunior, BOAT_SPECIES_NAMES, SHORE_SPECIES_NAMES),
    },
    official: {
      senior: buildRecordsIndex(officialSenior, BOAT_SPECIES_NAMES, SHORE_SPECIES_NAMES),
      junior: buildRecordsIndex(juniorOfficialEntries(), BOAT_SPECIES_NAMES, SHORE_SPECIES_NAMES),
    },
  };
  return cachedIndexes;
}

// Returns null, or an array of { scope, previous, recordType } entries — one
// per record (junior and/or senior) the catch beats or fills. Most catches
// only ever contend for one scope; a junior's C&R catch contends for both.
export function checkAgainstRecord(catchRow, indexes) {
  if (!indexes || !catchRow.species || !catchRow.location || catchRow.weight_kg == null) return null;
  const recordSet = catchRow.is_catch_and_release ? indexes.catchAndRelease : indexes.official;
  if (!recordSet) return null;
  const recordType = catchRow.is_catch_and_release ? "catchAndRelease" : "official";
  const results = recordScopesForCatch(catchRow)
    .map((scope) => {
      const match = matchRecordForIndex(recordSet[scope], catchRow, recordType);
      return match && { ...match, scope };
    })
    .filter(Boolean);
  return results.length ? results : null;
}
